import React, { useState } from 'react';
import { getMaterialInfo } from '../utils/helpers';
import { defaultMateriales } from '../mock/materiales'; // Para el dropdown de materiales

const ConsumptionModal = ({ operationId, initialConsumos, onUpdateConsumos, onClose }) => {
  const [currentConsumos, setCurrentConsumos] = useState(initialConsumos || []);
  const [showAddEditForm, setShowAddEditForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // null for add, index for edit
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [neededUnits, setNeededUnits] = useState('');
  const [message, setMessage] = useState('');

  const handleAddEditMaterial = () => {
    if (!selectedMaterialId || neededUnits === '' || isNaN(neededUnits) || parseInt(neededUnits) <= 0) {
      setMessage('Por favor, completa todos los campos con valores válidos.');
      return;
    }

    const newMaterialEntry = {
      materialId: selectedMaterialId,
      unidadesNecesarias: parseInt(neededUnits),
    };

    let updatedConsumos;
    if (editingIndex !== null) {
      // Editando
      updatedConsumos = currentConsumos.map((item, idx) =>
        idx === editingIndex ? newMaterialEntry : item
      );
    } else {
      // Añadiendo, verificar si ya existe el material
      const existingMaterialIndex = currentConsumos.findIndex(c => c.materialId === selectedMaterialId);
      if (existingMaterialIndex !== -1) {
        setMessage('Este material ya está en la lista. Edítalo si quieres cambiar las unidades.');
        return;
      }
      updatedConsumos = [...currentConsumos, newMaterialEntry];
    }

    setCurrentConsumos(updatedConsumos);
    setShowAddEditForm(false);
    setEditingIndex(null);
    setSelectedMaterialId('');
    setNeededUnits('');
    setMessage('');
  };

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setSelectedMaterialId(currentConsumos[index].materialId);
    setNeededUnits(currentConsumos[index].unidadesNecesarias);
    setShowAddEditForm(true);
  };

  const handleDeleteClick = (index) => {
    const updatedConsumos = currentConsumos.filter((_, idx) => idx !== index);
    setCurrentConsumos(updatedConsumos);
  };

  const handleSave = () => {
    onUpdateConsumos(operationId, currentConsumos);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white p-6 rounded-2xl shadow-xl max-w-2xl w-full transform transition-all scale-100 opacity-100 duration-300">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Consumos de Operación: {operationId}</h2>

        <div className="mb-6 max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
          {currentConsumos.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hay materiales registrados para esta operación.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidades</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentConsumos.map((consumo, index) => {
                  const material = getMaterialInfo(consumo.materialId, defaultMateriales);
                  return (
                    <tr key={consumo.materialId || index}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{material?.codigo || 'N/A'}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{material?.descripcion || 'Material Desconocido'}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{consumo.unidadesNecesarias}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditClick(index)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Sustituir
                        </button>
                        <button
                          onClick={() => handleDeleteClick(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {showAddEditForm ? (
          <div className="border border-gray-300 p-4 rounded-lg space-y-4">
            <h3 className="text-lg font-bold text-gray-900">{editingIndex !== null ? 'Editar Material' : 'Añadir Nuevo Material'}</h3>
            <div>
              <label htmlFor="material-select" className="block text-gray-700 text-sm font-medium mb-2">Material:</label>
              <select
                id="material-select"
                value={selectedMaterialId}
                onChange={(e) => setSelectedMaterialId(e.target.value)}
                disabled={editingIndex !== null} // No permitir cambiar material al editar
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">-- Selecciona un material --</option>
                {defaultMateriales.map(mat => (
                  <option key={mat.id} value={mat.id}>{mat.codigo} - {mat.descripcion}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="needed-units" className="block text-gray-700 text-sm font-medium mb-2">Unidades Necesarias:</label>
              <input
                id="needed-units"
                type="number"
                value={neededUnits}
                onChange={(e) => setNeededUnits(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            {message && <p className="text-red-500 text-sm mt-2">{message}</p>}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowAddEditForm(false); setEditingIndex(null); setMessage(''); setSelectedMaterialId(''); setNeededUnits(''); }}
                className="py-2 px-4 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddEditMaterial}
                className="py-2 px-4 rounded-lg bg-black text-white hover:bg-gray-800"
              >
                {editingIndex !== null ? 'Guardar Cambios' : 'Añadir Material'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddEditForm(true)}
            className="w-full py-2 px-4 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 mt-4"
          >
            Añadir Nuevo Material
          </button>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="py-3 px-5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors font-semibold text-base"
          >
            Cerrar
          </button>
          <button
            onClick={handleSave}
            className="py-3 px-5 rounded-lg bg-black text-white hover:bg-gray-800 transition-colors font-semibold text-base"
          >
            Guardar Consumos
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsumptionModal;